import { useState } from "react";
import { postsService } from "../services/postsService";
import { useAppStore } from "@/core/store/useAppStore";
import { postCacheUtils } from "./usePostsQuery";

interface UseDeletePostOptions {
  onSuccess?: (postId: string, response: any) => void;
  onError?: (error: string) => void;
}

export const useDeletePost = (options: UseDeletePostOptions = {}) => {
  const selectedClient = useAppStore((state) => state.selectedClient);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deletePost = async (postId: string): Promise<boolean> => {
    if (!selectedClient?.id) {
      const errorMsg = "No client selected";
      setError(errorMsg);
      options.onError?.(errorMsg);
      return false;
    }

    setIsDeleting(true);
    setError(null);

    try {
      const response = await postsService.deletePost(selectedClient.id, postId);

      if (response.success) {
        // Remove from React Query cache immediately
        postCacheUtils.removePost(selectedClient.id, postId);
        options.onSuccess?.(postId, response);
        return true;
      } else {
        const errorMsg = response.message || "Failed to delete post";
        setError(errorMsg);
        options.onError?.(errorMsg);
        return false;
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to delete post";
      setError(errorMessage);
      options.onError?.(errorMessage);
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    deletePost,
    isDeleting,
    error,
    clearError: () => setError(null),
  };
};
